import "./voiceplayer.css";

import { useTranslation } from "@next-platform/client-common-core";
import { Icon, IconVariants, Text } from "@next-platform/client-common-spicy-ui";
import type { TFunction } from "i18next";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { IValidationRef } from "@/pages/Voice/CloneVoice/components/CloneVoiceForm";
import { VOICE_CLIP_DURATION_ERROR, VoiceClipDurationError } from "@/pages/Voice/types";
import { cn } from "@/utils";

interface IVoiceClipProps {
  audioFile: File;
  isPlaying: boolean;
  handlePlayClick(name: string): void;
  handleDeleteClick(name: string): void;
}

interface IVoiceClipPlayerContentProps {
  audioFile: File;
  isPlaying: boolean;
  isValidFile: boolean;
  tooBigFile: boolean;
  stateDurationError: VoiceClipDurationError;
  stateDuration: number;
  refVoiceTrack: React.RefObject<HTMLInputElement | null>;
  t: TFunction;
  onAction(): void;
  onDelete(): void;
  onTrackChange(e: React.ChangeEvent<HTMLInputElement>): void;
}

const MIN = 5;
const MAX = 30;
const MAX_MEGABYTES = 4;
const BYTES_PER_KILOBYTE = 1024;
const BYTES_PER_MEGABYTE = BYTES_PER_KILOBYTE * BYTES_PER_KILOBYTE;
const MAX_FILE_SIZE = MAX_MEGABYTES * BYTES_PER_MEGABYTE;

function validateAudioLength(ln: number): VoiceClipDurationError {
  const duration = Math.floor(ln);

  if (duration < MIN) return VOICE_CLIP_DURATION_ERROR.TOO_SHORT;
  if (duration < MAX) return VOICE_CLIP_DURATION_ERROR.NONE;
  return VOICE_CLIP_DURATION_ERROR.TOO_LONG;
}

interface IFileStatusTextProps {
  tooBigFile: boolean;
  stateDurationError: VoiceClipDurationError;
  duration: number;
  t: TFunction;
}

const FileStatusText = ({ tooBigFile, stateDurationError, duration, t }: IFileStatusTextProps) => {
  const hasError = tooBigFile || stateDurationError !== VOICE_CLIP_DURATION_ERROR.NONE;
  const classname = "text-xs opacity-80 " + (hasError ? "text-red-9" : "text-gray-10");
  let text = Math.floor(duration) + "s";
  if (tooBigFile) {
    text = t("voice:form.error.tooLarge", { size: MAX_MEGABYTES });
  } else if (stateDurationError === VOICE_CLIP_DURATION_ERROR.TOO_SHORT) {
    text = t("voice:form.error.tooShort", { seconds: MIN });
  } else if (stateDurationError === VOICE_CLIP_DURATION_ERROR.TOO_LONG) {
    text = t("voice:form.error.tooLong", { seconds: MAX });
  }

  return <Text className={classname}>{text}</Text>;
};

const VoiceClipPlayerContent = ({
  audioFile,
  isPlaying,
  isValidFile,
  tooBigFile,
  stateDurationError,
  stateDuration,
  refVoiceTrack,
  t,
  onAction,
  onDelete,
  onTrackChange,
}: IVoiceClipPlayerContentProps) => {
  return (
    <div className="p-2 border border-solid border-gray-9 rounded-md ">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2.5 overflow-hidden">
          <div
            onClick={isValidFile ? onAction : undefined}
            className={cn(
              "bg-black shrink-0 dark:!bg-white !border-transparent size-9 rounded-full grid place-content-center",
              isValidFile ? "cursor-pointer" : "cursor-not-allowed",
            )}
          >
            <Icon icon={isPlaying ? IconVariants.PAUSE : IconVariants.PLAY} className="fill-white dark:fill-black dark:stroke-black" />
          </div>
          <div className="flex flex-col overflow-hidden">
            <Text className="opacity-80 text-sm leading-6 text-nowrap text-ellipsis overflow-hidden">{audioFile.name}</Text>
            <FileStatusText tooBigFile={tooBigFile} stateDurationError={stateDurationError} duration={stateDuration} t={t} />
          </div>
        </div>
        <div className="ml-3 cursor-pointer" onClick={onDelete}>
          <Icon icon={IconVariants.TRASH} className="stroke-gray-10" />
        </div>
      </div>
      <input
        defaultValue={0}
        disabled={!isValidFile}
        onChange={onTrackChange}
        min={0}
        ref={refVoiceTrack}
        type="range"
        className="voiceplayer accent-blue-10 w-full"
      />
    </div>
  );
};

const VoiceClipPlayer = (
  { audioFile, isPlaying, handlePlayClick, handleDeleteClick }: IVoiceClipProps,
  clipsFormRef: React.ForwardedRef<IValidationRef>,
) => {
  const { t } = useTranslation();
  const refObjectAudio = useRef<HTMLAudioElement | null>(null);
  const refVoiceTrack = useRef<HTMLInputElement>(null);
  const [stateDurationError, setStateDurationError] = useState<VoiceClipDurationError>(VOICE_CLIP_DURATION_ERROR.NONE);
  const [stateDuration, setStateDuration] = useState(0);
  const refHandlePlayClick = useRef(handlePlayClick);
  refHandlePlayClick.current = handlePlayClick;

  const tooBigFile = audioFile.size > MAX_FILE_SIZE;
  const isValidFile = stateDurationError === VOICE_CLIP_DURATION_ERROR.NONE && !tooBigFile;

  useEffect(() => {
    const audiourl = URL.createObjectURL(audioFile);
    const audio = refObjectAudio.current ?? new Audio(audiourl);

    const controller = new AbortController();
    refObjectAudio.current = audio;
    audio.addEventListener(
      "loadedmetadata",
      () => {
        setStateDurationError(validateAudioLength(audio.duration));
        setStateDuration(audio.duration);
        if (refVoiceTrack.current) refVoiceTrack.current.max = String(audio.duration);
      },
      { signal: controller.signal },
    );

    audio.addEventListener(
      "timeupdate",
      () => {
        if (refVoiceTrack.current) refVoiceTrack.current.value = String(refObjectAudio.current?.currentTime);
      },
      { signal: controller.signal },
    );
    audio.addEventListener(
      "ended",
      () => {
        refHandlePlayClick.current("");
        if (refObjectAudio.current) refObjectAudio.current.currentTime = 0;
      },
      { signal: controller.signal },
    );

    return () => {
      controller.abort();
      URL.revokeObjectURL(audiourl);
      refObjectAudio.current?.pause();
      refObjectAudio.current = null;
    };
  }, [audioFile]);

  useEffect(() => {
    if (!isPlaying) refObjectAudio.current?.pause();
  }, [isPlaying]);

  useEffect(() => {
    if (typeof clipsFormRef !== "function" && clipsFormRef?.current) clipsFormRef.current.clips[audioFile.name] = isValidFile;

    const currentRef = typeof clipsFormRef !== "function" ? clipsFormRef?.current : null;
    return () => {
      if (currentRef) delete currentRef.clips[audioFile.name];
    };
  }, [audioFile.name, isValidFile, clipsFormRef]);

  const handleAction = useCallback(() => {
    if (!refObjectAudio.current || !isValidFile) return;
    if (isPlaying) refObjectAudio.current?.pause();
    else refObjectAudio.current.play().catch(() => {});
    refHandlePlayClick.current(audioFile.name);
  }, [audioFile.name, isPlaying, isValidFile]);

  const handleTrackChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.valueAsNumber;
    if (refObjectAudio.current) refObjectAudio.current.currentTime = newVal;
  }, []);

  const onDelete = useCallback(() => handleDeleteClick(audioFile.name), [handleDeleteClick, audioFile.name]);

  return (
    <VoiceClipPlayerContent
      audioFile={audioFile}
      isPlaying={isPlaying}
      isValidFile={isValidFile}
      tooBigFile={tooBigFile}
      stateDurationError={stateDurationError}
      stateDuration={stateDuration}
      refVoiceTrack={refVoiceTrack}
      t={t}
      onAction={handleAction}
      onDelete={onDelete}
      onTrackChange={handleTrackChange}
    />
  );
};
export default React.forwardRef(VoiceClipPlayer);
